import { useState } from 'react';
import { MAX_CUSTOM_CSS } from '../../lib/sanitizeCss';
import { LAYOUTS, SCALE_STEPS, THEMES, useSettings } from '../../store/settings';
import { Choice, ghostButtonClass, Section } from './controls';
import { PresetsSection } from './PresetsSection';

const EXAMPLE = `/* Examples */
.glass { border-radius: 0.5rem; }
:root { --accent: #f472b6; }`;

export function AppearanceTab() {
  const layout = useSettings((s) => s.layout);
  const layoutPortrait = useSettings((s) => s.layoutPortrait);
  const theme = useSettings((s) => s.theme);
  const display = useSettings((s) => s.display);
  const customCss = useSettings((s) => s.customCss);
  const update = useSettings((s) => s.update);
  const reset = useSettings((s) => s.reset);
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <>
      <PresetsSection />
      <Section title="Layout">
        <Choice options={LAYOUTS} value={layout} onChange={(v) => update('layout', v)} />
      </Section>
      <Section
        title="On portrait screens"
        description="Used automatically when the screen is taller than it is wide – a rotated tablet or a portrait-mounted TV."
      >
        <Choice
          options={[
            ...LAYOUTS.filter((l) => l.id.startsWith('portrait')),
            { id: 'same' as const, name: 'Same as above', description: 'Don’t switch' },
          ]}
          value={layoutPortrait}
          onChange={(v) => update('layoutPortrait', v)}
          columns={3}
        />
      </Section>
      <Section
        title="Display size"
        description="Size of the clock, widgets and news. Auto keeps the same proportions on any screen, from a tablet to a 4K TV."
      >
        <Choice
          options={[
            { id: 'auto', name: 'Auto', description: 'Scales with the screen' },
            { id: 'fixed', name: 'Fixed', description: 'Same size everywhere' },
          ]}
          value={display.scaleMode}
          onChange={(scaleMode) => update('display', { scaleMode })}
        />
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Size">
          {SCALE_STEPS.map((s) => (
            <button
              key={s.value}
              role="radio"
              aria-checked={display.scale === s.value}
              onClick={() => update('display', { scale: s.value })}
              className={`rounded-xl border py-2 text-sm font-medium transition ${
                display.scale === s.value ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-[var(--border)]'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Theme">
        <Choice options={THEMES} value={theme} onChange={(v) => update('theme', v)} columns={3} />
      </Section>
      <Section
        title="Custom CSS"
        description="Advanced: override any style. Changes apply live. Imports, scripts and non-HTTPS images are removed for safety."
      >
        <textarea
          value={customCss}
          onChange={(e) => update('customCss', e.target.value.slice(0, MAX_CUSTOM_CSS))}
          placeholder={EXAMPLE}
          spellCheck={false}
          rows={10}
          className="w-full rounded-lg border border-[var(--border)] bg-black/30 p-3 font-mono text-xs outline-none focus:border-[var(--accent)]"
          aria-label="Custom CSS"
        />
        <div className="muted text-right text-xs">
          {customCss.length.toLocaleString()} / {MAX_CUSTOM_CSS.toLocaleString()}
        </div>
      </Section>
      <Section title="Reset">
        {confirmReset ? (
          <div className="flex gap-2">
            <button
              className={`${ghostButtonClass} border-red-400 text-red-400`}
              onClick={() => {
                reset();
                setConfirmReset(false);
              }}
            >
              Yes, reset all settings
            </button>
            <button className={ghostButtonClass} onClick={() => setConfirmReset(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <button className={ghostButtonClass} onClick={() => setConfirmReset(true)}>
            Reset settings on this screen
          </button>
        )}
      </Section>
    </>
  );
}
